import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Briefcase, Calendar, Clock, MessageSquare, Fingerprint } from "lucide-react";
import { fetchSellerProfile, fetchSellerAnnonces } from "@/lib/api";
import AnnonceCard from "@/components/AnnonceCard";

/** Public seller profile page : trust info + their active annonces. */
export default function SellerProfilePage() {
  const { userId } = useParams();
  const [seller, setSeller] = useState(null);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchSellerProfile(userId),
      fetchSellerAnnonces(userId),
    ])
      .then(([s, a]) => { setSeller(s); setAnnonces(a); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <main className="max-w-7xl mx-auto px-6 py-12 text-center" data-testid="seller-loading">Chargement…</main>;
  if (notFound || !seller) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-12 text-center" data-testid="seller-not-found">
        <h1 className="font-display text-3xl font-extrabold">Vendeur introuvable</h1>
        <Link to="/annonces" className="btn-primary mt-4 inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Retour aux annonces
        </Link>
      </main>
    );
  }

  const memberYear = (seller.member_since || "").slice(0, 4);
  const respTime = seller.response_time_minutes;
  const respLabel = respTime == null ? null : respTime < 60 ? `~${respTime} min` : `~${Math.round(respTime / 60)} h`;

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="seller-profile-page">
      <Link to="/annonces" className="text-sm text-[var(--ink-soft)] inline-flex items-center gap-1.5 mb-3">
        <ArrowLeft size={14} /> Retour
      </Link>

      <section className="glass-card p-7">
        <div className="flex items-center gap-5">
          {seller.picture ? (
            <img src={seller.picture} alt="" width="80" height="80" className="rounded-full object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-orange-500 text-white grid place-items-center font-bold text-3xl">
              {(seller.name || "?")[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">{seller.name}</h1>
            {memberYear && (
              <div className="text-sm text-[var(--ink-soft)] mt-1 inline-flex items-center gap-1.5">
                <Calendar size={14} /> Vendeur Fodindin depuis {memberYear}
                {seller.city && <> · {seller.city}</>}
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {seller.biometric_verified && (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-violet-600 to-purple-500 px-2.5 py-1 rounded-full shadow-sm"
                      data-testid="seller-page-biometric-verified"
                      title="Identite verifiee par empreinte digitale + selfie de reference (WebAuthn)">
                  <Fingerprint size={11} /> Identite biometrique verifiee
                </span>
              )}
              {seller.email_verified && (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200"
                      data-testid="seller-page-verified">
                  <ShieldCheck size={11} /> Email verifie
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <StatCard icon={Briefcase} label="Annonces actives" value={seller.total_annonces} />
          <StatCard icon={ShieldCheck} label="Verifiees" value={seller.verified_annonces} />
          {seller.response_rate_pct != null && (
            <StatCard icon={MessageSquare} label="Taux de reponse" value={`${seller.response_rate_pct}%`} />
          )}
          {respLabel && <StatCard icon={Clock} label="Delai moyen" value={respLabel} />}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-xl md:text-2xl font-extrabold tracking-tight mb-4">
          Annonces de {seller.name}
        </h2>
        {annonces.length === 0 ? (
          <p className="text-[var(--ink-soft)]" data-testid="seller-no-annonces">
            Aucune annonce active pour le moment.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="seller-annonces-grid">
            {annonces.map((a) => <AnnonceCard key={a.id} a={a} />)}
          </div>
        )}
      </section>
    </main>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="p-4 rounded-2xl border border-[var(--line)] bg-orange-50/40">
      <Icon size={16} className="text-orange-600" />
      <div className="text-xs text-[var(--ink-soft)] mt-2">{label}</div>
      <div className="font-extrabold text-xl mt-0.5">{value}</div>
    </div>
  );
}
